import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from "@heroui/react";

import { resetScreenTime, ScreenTimeResetResult } from '@/features/screentime/screenTimeApi';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';

export interface ScreenTimeResetProps {
  projectId:			string;
  roomNo:				string;
  isDisabled?:			boolean;
  // Called after a successful 204. Refetch rather than zeroing the panel
  // locally — the server is the only thing that knows what survived, and a room
  // that is still online may already have started recording again.
  onDone?:				() => any;
}

// Two-step confirm rather than a modal: the erase is irreversible, there is no
// soft delete and nothing lands in a recycle bin, so the second click has to be
// a deliberate act.
const ScreenTimeReset = (props: ScreenTimeResetProps) => {
  const { t } = useTranslation();

  const [confirming, setConfirming]	= React.useState(false);
  const [busy, setBusy]				= React.useState(false);
  const [error, setError]			= React.useState('');

  const fire = async () => {
    if (	busy ||
			!props.projectId ||
			!props.roomNo
		 )
      return;

    setBusy(true);
    setError('');

    const res: ScreenTimeResetResult = await resetScreenTime(props.projectId, props.roomNo);

    setBusy(false);
    setConfirming(false);

    if (res.kind === "ok") {
      if (props.onDone)
        props.onDone();
      return;
    }

    switch (res.kind) {
    case "managers_disabled":
      setError(t('projects:project:screentime:reset:managers_disabled'));
      break;
    case "no_project_access":
      setError(t('projects:project:screentime:reset:no_permission'));
      break;
    case "not_found":
      setError(t('projects:project:screentime:history:not_found'));
      break;
    case "disabled":
      setError(t('projects:project:screentime:reset:recording_off'));
      break;
    case "unauthorized":
      break;		// the session handler already redirects to login
    default:
      setError(t('projects:project:screentime:reset:failed'));
      break;
    }
  };

  return (
  <FlexRow direction="column"
           align="start"
           className="gap-[6px]">
  {!confirming &&
    <Button caption={t('projects:project:screentime:reset:caption')}
            icon="delete_sweep"
            layout="white"
            isDisabled={props.isDisabled || busy}
            onClick={() => { setError(''); setConfirming(true); }} />
  }
  {confirming &&
    <FlexRow className="gap-[8px]">
      <Button caption={t('projects:project:screentime:reset:confirm')}
              icon="warning_amber"
              layout="warning"
              isDisabled={busy}
              onClick={() => fire()} />
      <Button caption={t('projects:project:screentime:reset:cancel')}
              layout="white"
              isDisabled={busy}
              onClick={() => setConfirming(false)} />
    {busy &&
      <CircularProgress aria-label="Resetting screen time" size="sm" />
    }
    </FlexRow>
  }
  {confirming && !busy &&
    <span className="text-[--Dark-Grey] text-[12px] leading-[16px] max-w-[420px]">
      { t('projects:project:screentime:reset:hint') }
    </span>
  }
  {error != '' &&
    <span className="text-[12px] text-[--Error] leading-[16px] max-w-[420px]">
      { error }
    </span>
  }
  </FlexRow>
  );
};

export default ScreenTimeReset;
